import React, { useContext } from 'react';
import i18next from 'core/i18n';
import { useMutation } from '@apollo/client';

import {
  AgFinancingFileFolder,
  AgFinancingInput,
  AgGuarantorProcess,
  CreditInput,
  Phase1Input
} from '@indigo-ag/schema';
import { MediumTitle } from '@indigo-ag/component-module';

import { EnhancedExpansionPanel, PhaseSection } from 'shared';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { GuarantorBackgroundCheck, GuarantorCreditAnalysis } from './components';
import { upsertFinancingAgFinancing } from 'features/brazil-grower/queries';
import { uploadFilesToAgFinancing } from 'features/brazil-grower/components/BrazilGrowerFinance/utils';

export interface GuarantorsSectionProps {
  guarantorsInfo: AgGuarantorProcess[];
  disabled?: boolean;
}

export const GuarantorsSection: React.FunctionComponent<GuarantorsSectionProps> = ({
  guarantorsInfo,
  disabled = false
}) => {
  /**
   * Mutations
   */
  const [upsertFinancingAgFinancingFn] = useMutation(upsertFinancingAgFinancing);

  /**
   * Finance Process Context
   */
  const { agFinancingId } = useContext(FinanceProcessContext);

  const uploadFilesToPhase = uploadFilesToAgFinancing({
    agFinancingId,
    folder: AgFinancingFileFolder.GUARANTOR
  });

  const update = (guarantorP: AgGuarantorProcess) => ({
    phase1: (updatedPhase: Partial<Phase1Input>) => {
      const newAgFinancingData: Partial<AgFinancingInput> = {
        id: agFinancingId,
        guarantorProcess: {
          id: guarantorP.id,
          phase1: {
            ...updatedPhase
          }
        }
      };

      return upsertFinancingAgFinancingFn({
        variables: {
          data: newAgFinancingData
        }
      });
    },
    credit: (updatedCredit: Partial<CreditInput>) => {
      const newAgFinancingData: Partial<AgFinancingInput> = {
        id: agFinancingId,
        guarantorProcess: {
          id: guarantorP.id,
          credit: {
            ...updatedCredit
          }
        }
      };

      return upsertFinancingAgFinancingFn({
        variables: {
          data: newAgFinancingData
        }
      });
    }
  });

  return (
    <PhaseSection title={i18next.t('guarantorProcesses')} subtitle={i18next.t('guarantorDescription')}>
      {guarantorsInfo.map(g => (
        <EnhancedExpansionPanel
          key={`guarantor-${g.id}`}
          title={<MediumTitle className="text-transform-capitalize">{g.guaranteedBy.name.toLowerCase()}</MediumTitle>}
        >
          <div className="width-100">
            <GuarantorBackgroundCheck
              guarantorProcess={g}
              updateActions={{
                updateGuarantorProcess: update(g).phase1,
                toUploadFiles: uploadFilesToPhase
              }}
              disabled={disabled}
            />
            <GuarantorCreditAnalysis
              guarantorProcess={g}
              updateGuarantorProcess={update(g).credit}
              disabled={disabled}
            />
          </div>
        </EnhancedExpansionPanel>
      ))}
    </PhaseSection>
  );
};
